"use client";

/**
 * Full-screen Enrollment Preview surface (spec §6, Figma `1678:895`).
 *
 * Kept for back-compat with the dedicated route created by Phase I:
 *
 *   /jobs/[jobId]/sequences/[sequenceId]/enroll/preview/[previewSessionId]
 *
 * The primary surface is now `EnrollmentPreviewDrawer` (`./drawer`) — both
 * render the same shared `PreviewContent` body, so the orchestration logic
 * (commit / discard / patch, bucket URL state, candidate selection, 409
 * mapping) lives in exactly one place. This wrapper only supplies the
 * route-navigation callbacks the page needs:
 *
 *   - close / discard → navigate back to the sequence detail page.
 *   - commit success  → navigate to the sequence's Enrollments tab.
 */

import * as React from "react";
import { useRouter } from "next/navigation";

import { PreviewContent } from "./preview-content";

export interface PreviewProps {
  jobId: string;
  sequenceId: string;
  previewSessionId: string;
}

export function Preview({
  jobId,
  sequenceId,
  previewSessionId,
}: PreviewProps) {
  const router = useRouter();

  const navigateBack = React.useCallback(() => {
    router.replace(`/jobs/${jobId}/sequences/${sequenceId}`);
  }, [router, jobId, sequenceId]);

  const navigateToEnrollments = React.useCallback(() => {
    router.replace(`/jobs/${jobId}/sequences/${sequenceId}?tab=enrollments`);
  }, [router, jobId, sequenceId]);

  return (
    <div className="h-screen w-full">
      <PreviewContent
        jobId={jobId}
        sequenceId={sequenceId}
        previewSessionId={previewSessionId}
        onClose={navigateBack}
        onCommitted={navigateToEnrollments}
      />
    </div>
  );
}

export default Preview;
